import { useEffect, useMemo } from "react";

import type { CNSProjectAnalytics } from "@/models/cns";
import { useProjectDashboardStore } from "@/stores/useProjectDashboardStore";

export interface ProjectAnalyticsState extends CNSProjectAnalytics {
  loading: boolean;
  error: string | null;
}

const empty: CNSProjectAnalytics = {
  chatsStarted: 0,
  selectedAssumptions: 0,
  selectedExperiments: 0,
  learningsSynthesized: 0,
  insightsGenerated: 0,
  documentsAdded: 0,
  medianLearningCycleDays: null,
  learningCycleSampleSize: 0,
};

/**
 * Subscribes to per-project analytics in {@link useProjectDashboardStore}.
 *
 * Triggers a load on mount (and whenever {@code projectId} changes); the store
 * dedupes concurrent fetches and caches results across mounts so navigating
 * between tabs in the same project does not refetch.
 *
 * Pass {@code null} when there is no project context (returns the empty state
 * without a network call).
 */
export function useProjectAnalytics(projectId: string | null): ProjectAnalyticsState {
  const slice = useProjectDashboardStore((s) =>
    projectId ? s.byProject[projectId]?.analytics : undefined,
  );
  const loadAnalytics = useProjectDashboardStore((s) => s.loadAnalytics);

  useEffect(() => {
    if (!projectId) return;
    void loadAnalytics(projectId);
  }, [projectId, loadAnalytics]);

  return useMemo(() => {
    if (!projectId) {
      return { ...empty, loading: false, error: null };
    }
    return {
      ...(slice?.data ?? empty),
      loading: slice?.loading ?? true,
      error: slice?.error ?? null,
    };
  }, [projectId, slice]);
}
